// src/pages/ingresos/IntakeReceiptPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { supabase } from "../../utils/supabase";

// Igual que en Facturación
const LOGO_URL =
  "https://qmrmkemkctikfoyxpanz.supabase.co/storage/v1/object/public/personalizacion/Logo%20Calins.png";

// Lee ?id=... de la URL
function getIntakeIdFromUrl() {
  const qp = new URLSearchParams(window.location.search);
  return qp.get("id");
}

async function loadImageDataUrl(url) {
  const res = await fetch(url, { cache: "no-cache" });
  if (!res.ok) throw new Error(`No se pudo cargar la imagen del logo: ${url}`);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function drawPdfFrame(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setLineWidth(0.3);
  doc.rect(6, 6, pageWidth - 12, pageHeight - 12);
}

function drawPdfLine(doc, x, y, w, label, value) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.text(`${label}:`, x, y);
  doc.setFont("helvetica", "normal");
  doc.text(String(value || "—"), x + w, y);
}

function drawSignatureBlock(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const top = pageHeight - 42;
  const blockW = (pageWidth - 24) / 2;
  const leftX = 12;
  const rightX = leftX + blockW + 6;

  const drawBlock = (x, title) => {
    doc.setLineWidth(0.25);
    doc.rect(x, top, blockW, 26);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text(title, x + blockW / 2, top + 5, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.text("Firma y aclaración", x + blockW / 2, top + 16, { align: "center" });
    doc.line(x + 10, top + 20, x + blockW - 10, top + 20);
  };

  drawBlock(leftX, "Cliente");
  drawBlock(rightX, "Laboratorio");
}

async function drawReceiptCopyPdf(doc, payload) {
  const {
    logoDataUrl,
    intake,
    fechaRecepcion,
    horaRecepcion,
    clienteNombre,
    clienteCuit,
    clienteNumero,
    clienteDireccion,
    tipoCopia,
    items,
  } = payload;

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 10;
  const tableStartY = 65;
  const footerReserve = 48;

  const header = () => {
    drawPdfFrame(doc);

    if (logoDataUrl) {
      try {
        doc.addImage(logoDataUrl, "PNG", marginX, 9, 32, 13);
      } catch {
        // Si el logo falla, seguimos sin bloquear el PDF.
      }
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Comprobante de Recepción de Instrumentos", pageWidth / 2, 16, {
      align: "center",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.2);
    doc.text(tipoCopia, pageWidth / 2, 24, { align: "center" });

    doc.setFontSize(8);
    doc.text(`Ingreso N°: ${intake?.code || intake?.id || "—"}`, pageWidth - marginX, 14, {
      align: "right",
    });
    doc.text(`Fecha: ${fechaRecepcion}`, pageWidth - marginX, 19, { align: "right" });
    if (horaRecepcion) {
      doc.text(`Hora: ${horaRecepcion}`, pageWidth - marginX, 24, { align: "right" });
    }

    doc.setLineWidth(0.2);
    doc.rect(marginX, 28, pageWidth - marginX * 2, 28);
    doc.line(pageWidth / 2, 28, pageWidth / 2, 56);

    const leftX = marginX + 3;
    const rightX = pageWidth / 2 + 3;
    const valueOffset = 22;

    drawPdfLine(doc, leftX, 35, valueOffset, "Cliente", clienteNombre);
    drawPdfLine(doc, leftX, 40, valueOffset, "CUIT", clienteCuit);
    drawPdfLine(doc, leftX, 45, valueOffset, "Cliente N°", clienteNumero);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("Dirección:", leftX, 50);
    doc.setFont("helvetica", "normal");
    const direccionLines = doc.splitTextToSize(String(clienteDireccion || "—"), pageWidth / 2 - 10).slice(0, 2);
    doc.text(direccionLines, leftX + valueOffset, 50);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("Persona que entrega:", rightX, 35);
    doc.text("__________________", rightX + 40, 35);
    doc.text("Persona que recibe:", rightX, 40);
    doc.text("__________________", rightX + 40, 40);
    doc.text("Documento / Legajo:", rightX, 45);
    doc.text("__________________", rightX + 40, 45);
  };

  autoTable(doc, {
    head: [["N°", "TAG / LAB", "DESCRIPCIÓN", "N° DE SERIE", "OBSERVACIONES"]],
    body: items.map((r) => [String(r.nro), r.tag, r.descripcion, r.serie, r.nota || ""]),
    startY: tableStartY,
    margin: { left: marginX, right: marginX, top: tableStartY, bottom: footerReserve },
    theme: "grid",
    pageBreak: "auto",
    rowPageBreak: "avoid",
    styles: {
      fontSize: 7.8,
      cellPadding: 1.2,
      valign: "middle",
      lineWidth: 0.2,
      lineColor: [160, 160, 160],
    },
    headStyles: {
      fillColor: [219, 234, 254],
      textColor: [17, 24, 39],
      fontStyle: "bold",
    },
    columnStyles: {
      0: { cellWidth: 10 },
      1: { cellWidth: 33 },
      2: { cellWidth: 76 },
      3: { cellWidth: 29 },
      4: { cellWidth: 32 },
    },
    didDrawPage: () => {
      header();
    },
  });

  drawSignatureBlock(doc);
}

export default function IntakeReceiptPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [intake, setIntake] = useState(null);
  const [client, setClient] = useState(null);
  const [items, setItems] = useState([]);

  const contentRef = useRef(null);
  const intakeId = getIntakeIdFromUrl();

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        if (!intakeId) throw new Error("Falta ?id con el ID del ingreso (intake).");

        // 1) Ingreso (intakes)
        const { data: intakeRow, error: eIntake } = await supabase
          .from("intakes")
          .select("id, code, reception_date, created_at, client_id")
          .eq("id", intakeId)
          .single();

        if (eIntake) throw eIntake;
        if (!intakeRow) throw new Error("Ingreso no encontrado.");

        // 2) Cliente
        let clientRow = null;
        if (intakeRow.client_id) {
          const { data: cli, error: eCli } = await supabase
            .from("clients")
            .select("id, name, client_number, cuit, tax_id, address")
            .eq("id", intakeRow.client_id)
            .single();
          if (eCli) throw eCli;
          clientRow = cli;
        }

        // 3) Instrumentos del intake
        const { data: itemRows, error: eItems } = await supabase
          .from("intake_items")
          .select(
            `
            id,
            created_at,
            note,
            instruments (
              id,
              lab_id,
              tag,
              brand,
              model,
              serial,
              type,
              unit,
              instrument_categories ( name )
            )
          `
          )
          .eq("intake_id", intakeId)
          .order("created_at", { ascending: true });

        if (eItems) throw eItems;

        if (!active) return;
        setIntake(intakeRow);
        setClient(clientRow);
        setItems(itemRows || []);
      } catch (err) {
        if (!active) return;
        setError(err.message ?? String(err));
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [intakeId]);

  const preparedItems = useMemo(
    () =>
      (items || []).map((it, idx) => {
        const inst = it.instruments || {};
        const tag =
          inst.tag || (inst.lab_id ? `LAB ${inst.lab_id}` : `Inst. ${idx + 1}`);
        const descParts = [];

        // ✅ Preferimos categoría (ej: "Manómetro") por sobre familia/variable (ej: "PRESSURE")
        const categoryName = inst.instrument_categories?.name;
        if (categoryName) descParts.push(categoryName);
        else if (inst.type) descParts.push(inst.type.toUpperCase());

        if (inst.brand) descParts.push(inst.brand);
        if (inst.model) descParts.push(inst.model);

        const descripcion = descParts.join(" - ") || "Instrumento sin descripción";

        return {
          id: it.id,
          nro: idx + 1,
          tag,
          serie: inst.serial || "—",
          descripcion,
          nota: it.note || "",
        };
      }),
    [items]
  );

  async function handleExportPDF() {
    try {
      const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      const logoDataUrl = await loadImageDataUrl(LOGO_URL);

      await drawReceiptCopyPdf(doc, {
        logoDataUrl,
        intake,
        fechaRecepcion,
        horaRecepcion,
        clienteNombre,
        clienteCuit,
        clienteNumero,
        clienteDireccion,
        tipoCopia: "Copia para el laboratorio",
        items: preparedItems,
      });

      doc.addPage();

      await drawReceiptCopyPdf(doc, {
        logoDataUrl,
        intake,
        fechaRecepcion,
        horaRecepcion,
        clienteNombre,
        clienteCuit,
        clienteNumero,
        clienteDireccion,
        tipoCopia: "Copia para el cliente",
        items: preparedItems,
      });

      doc.save(`Comprobante_Ingreso_${intake?.code || intakeId}.pdf`);
    } catch (e) {
      console.error(e);
      alert("No se pudo generar el PDF.");
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen grid place-content-center text-gray-700">
        Cargando comprobante de ingreso…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-content-center text-red-700">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 max-w-lg">
          <div className="font-semibold mb-1">No pudimos cargar la información</div>
          <div className="text-sm">{error}</div>
        </div>
      </div>
    );
  }

  const fechaRecepcion = intake?.reception_date || intake?.created_at
    ? new Date(`${String(intake.reception_date || intake.created_at).slice(0, 10)}T12:00:00`).toLocaleDateString("es-AR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
    : new Date().toLocaleDateString("es-AR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });

  const horaRecepcion = intake?.created_at
    ? new Date(intake.created_at).toLocaleTimeString("es-AR", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const clienteNombre = client?.name ?? "—";
  const clienteCuit = client?.cuit || client?.tax_id || "—";
  const clienteNumero = client?.client_number ?? "—";
  const clienteDireccion = client?.address ?? "—";

  return (
    <div className="min-h-screen bg-gray-100 py-6">
      <div className="max-w-5xl mx-auto">
        {/* Barra acciones (no se imprime en PDF final) */}
        <div
          id="actions-bar"
          className="flex items-center justify-end gap-2 mb-3 print:hidden"
        >
          <button
            onClick={handleExportPDF}
            className="px-3 py-2 rounded-md bg-sky-600 text-white hover:bg-sky-700 text-sm"
          >
            Descargar PDF
          </button>
        </div>

        {/* Contenido imprimible */}
        <div
          ref={contentRef}
          className="bg-white shadow-lg rounded-xl overflow-hidden print:shadow-none print:rounded-none"
        >
          {/* Doble copia en una sola página */}
          <ReceiptBlock
            tipoCopia="COPIA PARA EL CLIENTE"
            logoUrl={LOGO_URL}
            intake={intake}
            fechaRecepcion={fechaRecepcion}
            horaRecepcion={horaRecepcion}
            clienteNombre={clienteNombre}
            clienteCuit={clienteCuit}
            clienteNumero={clienteNumero}
            clienteDireccion={clienteDireccion}
            items={preparedItems}
          />

          <div className="border-t border-dashed border-gray-300 my-2 mx-10" />

          <ReceiptBlock
            tipoCopia="COPIA PARA EL LABORATORIO"
            logoUrl={LOGO_URL}
            intake={intake}
            fechaRecepcion={fechaRecepcion}
            horaRecepcion={horaRecepcion}
            clienteNombre={clienteNombre}
            clienteCuit={clienteCuit}
            clienteNumero={clienteNumero}
            clienteDireccion={clienteDireccion}
            items={preparedItems}
          />

          <footer className="text-center text-xs text-gray-600 py-3 border-t border-gray-200 bg-gray-50">
            Calins Laboratorio · Comprobante de Ingreso — {new Date().getFullYear()}
          </footer>
        </div>
      </div>
    </div>
  );
}

/**
 * Bloque reutilizable para cada copia (cliente / laboratorio)
 */
function ReceiptBlock({
  tipoCopia,
  logoUrl,
  intake,
  fechaRecepcion,
  horaRecepcion,
  clienteNombre,
  clienteCuit,
  clienteNumero,
  clienteDireccion,
  items,
}) {
  return (
    <div className="px-6 pt-5 pb-4">
      {/* Header con logo y título */}
      <header className="border-b border-gray-200 pb-3 mb-3 flex flex-col md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <img src={logoUrl} alt="Logo" className="h-10 w-auto object-contain" />
          <div>
            <h1 className="text-lg font-semibold text-gray-900">
              Comprobante de Recepción de Instrumentos
            </h1>
            <p className="text-xs text-gray-600">{tipoCopia}</p>
          </div>
        </div>
        <div className="mt-3 md:mt-0 text-right text-xs text-gray-800 space-y-0.5">
          <p>
            <b>Ingreso Nº:</b> {intake?.code || intake?.id || "—"}
          </p>
          <p>
            <b>Fecha:</b> {fechaRecepcion}
          </p>
          {horaRecepcion && (
            <p>
              <b>Hora:</b> {horaRecepcion}
            </p>
          )}
        </div>
      </header>

      {/* Datos del cliente / recepción */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-800 mb-3">
        <div className="space-y-0.5">
          <div>
            <span className="text-gray-700">Cliente:</span> {clienteNombre}
          </div>
          <div>
            <span className="text-gray-700">CUIT:</span> {clienteCuit}
          </div>
          <div>
            <span className="text-gray-700">Cliente Nº:</span> {clienteNumero}
          </div>
          <div>
            <span className="text-gray-700">Dirección:</span> {clienteDireccion}
          </div>
        </div>
        <div className="space-y-0.5 md:text-right">
          <div>
            <span className="text-gray-700">Persona que entrega:</span>{" "}
            _______________________
          </div>
          <div>
            <span className="text-gray-700">Persona que recibe:</span>{" "}
            _______________________
          </div>
          <div>
            <span className="text-gray-700">Documento / Legajo:</span>{" "}
            _______________________
          </div>
        </div>
      </section>

      {/* Tabla de instrumentos */}
      <section className="text-xs text-gray-900 mb-3">
        <div className="overflow-visible rounded-md border border-gray-200">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="bg-blue-100 text-gray-900">
                <th className="px-2 py-1 text-left w-10">Nº</th>
                <th className="px-2 py-1 text-left w-32">TAG / LAB</th>
                <th className="px-2 py-1 text-left">DESCRIPCIÓN</th>
                <th className="px-2 py-1 text-left w-32">Nº DE SERIE</th>
                <th className="px-2 py-1 text-left w-40">OBSERVACIONES</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-2 py-2 text-center text-gray-500">
                    No hay instrumentos cargados en este ingreso.
                  </td>
                </tr>
              ) : (
                items.map((r) => (
                  <tr
                    key={r.id}
                    className={
                      r.nro % 2 === 0
                        ? "bg-gray-50 text-gray-800"
                        : "bg-white text-gray-800"
                    }
                  >
                    <td className="px-2 py-1">{r.nro}</td>
                    <td className="px-2 py-1">{r.tag}</td>
                    <td className="px-2 py-1">{r.descripcion}</td>
                    <td className="px-2 py-1">{r.serie}</td>
                    <td className="px-2 py-1">{r.nota}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Firmas */}
      <section className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-gray-800">
        <div className="text-center">
          <div className="h-10" />
          <div className="border-t border-gray-400 mx-10" />
          <div className="mt-1">Firma y aclaración del Cliente</div>
        </div>
        <div className="text-center">
          <div className="h-10" />
          <div className="border-t border-gray-400 mx-10" />
          <div className="mt-1">Firma y sello del Laboratorio</div>
        </div>
      </section>
    </div>
  );
}
